/**
 * @format
 */
import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from 'react';
import { loginUser, signupUser } from '../../core/services/keycloakApi';
import { getUserInfoFromToken, isTokenValid } from '../../core/utils/authUtils';
import {
  getUserErrorMessage,
  logApiError,
} from '../../core/utils/apiErrorUtils';
import { createNewUser } from '../../core/services/profile.service';
import { CLIENT_NAME, REALM } from '../../core/constants/keycloakCredentials';
import { getAllPermissions } from '../../core/services/admin.service';
import { MODULE_LIST } from '../../constants/commons';

const AuthContext = createContext();

export const useAuth = () => {
  return useContext(AuthContext);
};

export const AuthProvider = ({ children }) => {
  const [userInfo, setUserInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [userPermissions, setUserPermissions] = useState({});

  const initializeAuth = useCallback(() => {
    try {
      const token = localStorage.getItem('authToken');

      if (token && isTokenValid(token)) {
        const userData = getUserInfoFromToken(token);
        if (userData) {
          setUserInfo(userData);
        } else {
          localStorage.removeItem('authToken');
        }
      }
    } catch (error) {
      localStorage.clear();
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    initializeAuth();
  }, [initializeAuth]);

  const login = async (email, password) => {
    try {
      setLoading(true);
      const response = await loginUser(email, password);
      if (response.data?.data?.access_token) {
        const token = response.data.data.access_token;
        await getPermissions(token);

        localStorage.setItem('authToken', token);
        localStorage.setItem('refreshToken', response.data.data.refresh_token);

        const userData = getUserInfoFromToken(token);

        setUserInfo(userData);

        return { success: true, user: userData };
      }

      return { success: false, error: 'Invalid credentials' };
    } catch (error) {
      logApiError('Login', error);
      return {
        success: false,
        error: getUserErrorMessage(
          error,
          "We couldn't sign you in. Please check your credentials and try again."
        ),
      };
    } finally {
      setLoading(false);
    }
  };

  const signup = async (userData) => {
    try {
      setLoading(true);

      const { data, success, message } = await signupUser(userData);
      const payload = {
        userName: data.username,
        userId: data.id,
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        clientName: CLIENT_NAME,
        realName: REALM,
        redirectUrl: process.env.REACT_APP_DOMAIN + '/users',
      };
      const result = await createNewUser(payload);

      if (success === false) {
        return {
          success: false,
          error: 'Signup failed' || data.message,
        };
      }

      return {
        success: true,
        message: 'Account created successfully. Please login.' || message,
      };
    } catch (error) {
      return {
        success: false,
        error: 'User already exists' || error.response?.data?.message,
      };
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    setUserInfo(null);
    localStorage.clear();
    sessionStorage.clear();
  };

  function enableAll(data) {
    // Enable all permissions
    const permissions = Object.keys(MODULE_LIST).reduce((acc, key) => {
      acc[key] = {
        view: true,
        edit: true,
        delete: true,
        update: true,
      };
      return acc;
    }, {});
    return permissions;
  }

  // Permissions to what UI elements or screens the user is allowed to see.
  const getPermissions = async (token) => {
    try {
      const data = await getAllPermissions(
        token || localStorage.getItem('authToken')
      );
      setUserPermissions(enableAll(data));
    } catch (error) {}
  };

  useEffect(() => {
    getPermissions();
  }, []);

  const value = {
    userInfo,
    login,
    signup,
    logout,
    loading,
    isAuthenticated: !!userInfo && !loading,
    userPermissions,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
